/**
 * RainGuardSimulator - Simulador de ROI do protetor de chuva (rain guard)
 *
 * Usa o histórico REAL de dias/kg perdidos por chuva na safra (vindos do
 * fechamento) + inputs do usuário (custo/árvore, % recuperado, durabilidade)
 * para calcular investimento, retorno e payback.
 */
import React, { useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const fmtRs = (v) => `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtKg = (v) => `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;

export const RainGuardSimulator = ({ diasChuva, kgChuva, precoKg, totalPlantas }) => {
  const [expanded, setExpanded] = useState(false);
  const [custoArvore, setCustoArvore] = useState("2.50");
  const [pctRec, setPctRec] = useState("70");
  const [durabilidade, setDurabilidade] = useState("2");

  if (!kgChuva || !totalPlantas) return null;

  const custo = parseFloat(custoArvore) || 0;
  const pct = Math.min(100, Math.max(0, parseFloat(pctRec) || 0));
  const durab = Math.max(1, parseFloat(durabilidade) || 1);

  const investimento = custo * totalPlantas;
  const kgRecuperado = kgChuva * (pct / 100);
  const rsRecuperado = precoKg ? kgRecuperado * precoKg : null;
  const payback = rsRecuperado && rsRecuperado > 0 ? investimento / rsRecuperado : null;
  const lucro = rsRecuperado ? rsRecuperado * durab - investimento : null;

  return (
    <div className="border border-blue-200 bg-blue-50 rounded-lg px-3 py-2" data-testid="rainguard-simulator">
      <button className="w-full flex items-center justify-between text-sm font-semibold text-blue-800" onClick={() => setExpanded(!expanded)} data-testid="rainguard-toggle">
        <span>💧 Simulador: Protetor de Chuva (rain guard)</span>
        <span>{expanded ? "▲" : "▼"}</span>
      </button>
      <p className="text-[11px] text-blue-700 mt-1">
        Seu histórico: <strong>{diasChuva} dia(s)</strong> perdidos por chuva nesta safra ≈ <strong>{fmtKg(kgChuva)}</strong>
      </p>

      {expanded && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px]">Custo/árvore (R$)</Label>
              <Input type="number" min="0" step="0.10" className="h-8 text-sm" value={custoArvore}
                onChange={(e) => setCustoArvore(e.target.value)} data-testid="rainguard-custo" />
            </div>
            <div>
              <Label className="text-[11px]">% dias recuperados</Label>
              <Input type="number" min="0" max="100" step="5" className="h-8 text-sm" value={pctRec}
                onChange={(e) => setPctRec(e.target.value)} data-testid="rainguard-pct" />
            </div>
            <div>
              <Label className="text-[11px]">Durabilidade (safras)</Label>
              <Input type="number" min="1" step="1" className="h-8 text-sm" value={durabilidade}
                onChange={(e) => setDurabilidade(e.target.value)} data-testid="rainguard-durabilidade" />
            </div>
          </div>

          <div className="bg-white rounded border border-blue-200 px-3 py-2 text-sm space-y-1" data-testid="rainguard-resultado">
            <div className="flex flex-wrap justify-between items-start gap-x-2">
              <span className="text-gray-600 min-w-0">Investimento ({totalPlantas.toLocaleString("pt-BR")} árvores):</span>
              <span className="font-medium text-right ml-auto whitespace-nowrap">{fmtRs(investimento)}</span>
            </div>
            <div className="flex flex-wrap justify-between items-start gap-x-2">
              <span className="text-gray-600 min-w-0">Recupera por safra:</span>
              <span className="font-medium text-emerald-700 text-right ml-auto">
                <span className="whitespace-nowrap">{fmtKg(kgRecuperado)}</span>{rsRecuperado != null && <span className="whitespace-nowrap block sm:inline sm:ml-1">({fmtRs(rsRecuperado)})</span>}
              </span>
            </div>
            {payback != null && (
              <div className="flex flex-wrap justify-between items-start gap-x-2 font-semibold" data-testid="rainguard-payback">
                <span className="text-gray-800 min-w-0">🎯 Payback:</span>
                <span className={`text-right ml-auto whitespace-nowrap ${payback <= durab ? "text-emerald-700" : "text-red-600"}`}>
                  {payback.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} safra(s)
                </span>
              </div>
            )}
            {lucro != null && (
              <div className="flex flex-wrap justify-between items-start gap-x-2">
                <span className="text-gray-600 min-w-0">Resultado em {durab} safra(s):</span>
                <span className={`font-medium text-right ml-auto whitespace-nowrap ${lucro >= 0 ? "text-emerald-700" : "text-red-600"}`}>{lucro >= 0 ? "+" : ""}{fmtRs(lucro)}</span>
              </div>
            )}
            {rsRecuperado == null && (
              <p className="text-[11px] text-amber-700">⚠️ Informe o preço do kg na configuração (⚙️) para ver o payback em R$.</p>
            )}
          </div>
          <p className="text-[11px] text-blue-600">📐 Simulação com base no seu histórico real de chuva. Valores de custo são estimativas suas — consulte fornecedores.</p>
        </div>
      )}
    </div>
  );
};
